import { Box, Flex, TagLabel } from "@chakra-ui/react";
import { useField } from "formik";
import React from "react";
import {
  WEGLOT_APPLY_CLASS,
  WEGLOT_SKIP_CLASS,
} from "src/plugins/weglot/constants";
import { MultiSelectInput } from "../MultiSelectInput";
import { BaseInputProps, Option } from "./Question";
import { WithTranslation } from "./WithTranslation";

export interface MultiSelectProps extends BaseInputProps {
  options: Option[];
}

type MultiSelectOption = Omit<Option, "key"> & { id: string };

const toMultiSelectOption = (option: Option) => {
  const { key, ...rest } = option;
  return {
    ...rest,
    id: key,
  };
};

export const MultiSelect: React.FC<MultiSelectProps> = ({
  id,
  options,
  isDisabled,
}) => {
  return (
    <WithTranslation items={options}>
      {(translatedOptions) => (
        <InternalMultiSelect
          options={translatedOptions}
          id={id}
          isDisabled={isDisabled}
        />
      )}
    </WithTranslation>
  );
};

const InternalMultiSelect: React.FC<MultiSelectProps> = ({
  options,
  id,
  isDisabled,
}) => {
  const [field, meta, helpers] = useField<string[]>(id);

  const renderTagContent = React.useCallback((item: MultiSelectOption) => {
    const label = item.label;
    return (
      <TagLabel fontStyle={item.isArchived ? "italic" : "normal"}>
        <span className={WEGLOT_SKIP_CLASS}>{label}</span>{" "}
        {item.isArchived ? (
          <span className={WEGLOT_APPLY_CLASS}>(archived)</span>
        ) : null}
      </TagLabel>
    );
  }, []);

  // Only non-archived options for selection in dropdown
  const nonArchivedOptions = React.useMemo(
    () => options.filter((o) => !o.isArchived).map(toMultiSelectOption),
    [options]
  );

  const allOptions = React.useMemo(
    () => options.map(toMultiSelectOption),
    [options]
  );

  const initialValues = React.useMemo(
    () => allOptions.filter((o) => field.value?.includes(o.id)),
    [allOptions, field.value]
  );

  const onChangeHandler = (values: MultiSelectOption[]) => {
    // wrapping this on setTimeout to avoid weird bug where this trigger max update stack on react component.
    setTimeout(() => {
      helpers.setValue(
        values.map((o) => o.id),
        true
      );
      helpers.setTouched(true, false);
    }, 0);
  };
  return (
    <Flex
      direction="column"
      onBlur={() => {
        helpers.setTouched(true);
      }}
    >
      <MultiSelectInput<MultiSelectOption>
        size="md"
        options={nonArchivedOptions}
        initialValues={initialValues}
        onChange={onChangeHandler}
        isDisabled={isDisabled}
        error={meta.touched && meta.error ? meta.error : undefined}
        renderDropdownItem={(item) => {
          return <Box className={WEGLOT_SKIP_CLASS}>{item.label}</Box>;
        }}
        renderTagContent={renderTagContent}
      />
    </Flex>
  );
};
